import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, OnDestroy } from '@angular/core';

interface IOption {
    key: string;
    value: string;
    checked: boolean;
    hasSeparator: boolean;
}

@Component({
    selector: 'ui-grid-filters',
    templateUrl: './grid-filters.component.html',
    styleUrls: ['./grid-filters.component.less']
})
export class GridFiltersComponent implements OnInit, OnDestroy {
    @Output() applyFilters: EventEmitter<Array<string>> = new EventEmitter<Array<string>>();
    @Output() setMultiValue = new EventEmitter<string>();
    @Output() onCancel = new EventEmitter<void>();

    @Input() filtersList: Array<{[key: string]: string}> = [];
    @Input() selectedOptionsList: Array<string> = [];
    @Input() enableFiltering: boolean;
    @Input() enableActions = true;
    @Input() multiValuePrefix: string;
    @Input() multiValueSuffix: string;
    @Input() homeSiteId: number;
    @Input() isMultiSiteDropdown: boolean = false;

    @Input() dataFilterConfig: Record<string, any> = {
        filterLimit: 6
    };

    private minQueryLength: number = 1;
    defaultLabel = 'All';

    get isAllChecked(): boolean {
        return  this.innerOptionsList.every((option: IOption) => option.checked );
    }

    get isSomeChecked(): boolean {
        return this.innerOptionsList.some((option: IOption) => option.checked )
            && this.innerOptionsList.some((option: IOption) => !option.checked );
    }

    get hasFilteredResults(): boolean {
        return (!this.pipeFilterQuery.length)
            ? true
            : this.innerOptionsList.some((option: IOption) => option.value.toLowerCase().includes(this.pipeFilterQuery.toLowerCase()) );
    }

    constructor(private elementRef: ElementRef) { }

    formattedFiltersList: {[key: string]: string} = {};

    filterQuery = '';
    pipeFilterQuery = '';

    innerOptionsList: Array<IOption> = [];

    @HostListener('document:click', ['$event.target'])
    clickOutside(target: HTMLElement): void {}

    ngOnInit(): void {
        if (this.dataFilterConfig && this.dataFilterConfig.minHighlightLength) {
            this.minQueryLength = this.dataFilterConfig.minHighlightLength;
        }
        this.mapFiltersList();

        // add listener after component init
        setTimeout(() => { this.clickOutside = this.clickHandler; });
    }

    ngOnDestroy(): void {
        if (!this.enableActions) {
            const selectedOptionsList: Array<string> = this.innerOptionsList
                .filter((option: IOption) => option.checked)
                .map((option: IOption) => option.key);
            this.applyFilters.emit(selectedOptionsList);
        }
    }

    check(): void {
        const selectedOptionsList = this.innerOptionsList
            .filter((option: IOption) => option.checked);
        const length = selectedOptionsList.length;
        let result = '';

        this.setMultiValue.emit(length ? (length > 1 ? `${length} ${this.multiValueSuffix}` : selectedOptionsList[0].value) : '');

        if (length) {
            if (length === 1) {
                result = selectedOptionsList[0].value;
            }
            if (length > 1) {
                result = `${length} ${this.multiValueSuffix}`;
            }
            if (length === this.innerOptionsList.length) {
                result = this.defaultLabel;
            }
        } else {
            result = '';
        }
        this.setMultiValue.emit(result);

        if (!this.enableActions) {
            const selectedOptionsList: Array<string> = this.innerOptionsList // tslint:disable-line
                .filter((option: IOption) => option.checked)
                .map((option: IOption) => option.key);
            this.applyFilters.emit(selectedOptionsList);
        }
    }

    private clickHandler(target: HTMLElement): void {
        const clickedInside: boolean = this.elementRef.nativeElement.contains(target);
        if (!clickedInside) {
            console.log('GridFiltersComponent: clickOutside');
            this.dismiss();
        }
    }

    private mapFiltersList(): void {
        this.innerOptionsList = this.filtersList.map(({ key, value }) => {
            return ({
                key,
                value,
                checked: !this.selectedOptionsList.length && this.enableActions ? true : this.selectedOptionsList.includes(key),
                hasSeparator: false
            });
        });

        if (!this.isAllChecked) {
            const checkedList: Array<IOption> = this.innerOptionsList.filter((option: IOption) => option.checked);
            const notCheckedList: Array<IOption> = this.innerOptionsList.filter((option: IOption) => !option.checked);

            // add separator
            if (checkedList.length && notCheckedList.length) {
                checkedList[checkedList.length - 1].hasSeparator = true;
            }

            this.innerOptionsList = [...checkedList, ...notCheckedList];
        }

    }

    toggleAllChecked(): void {
        const IS_ALL_CHECKED: boolean = this.isSomeChecked ? true : !this.isAllChecked;
        this.innerOptionsList.forEach((option: IOption) => option.checked = IS_ALL_CHECKED);
    }

    dismiss(): void {
        /**
         * multi site selection Cancel button behavior equals to offclick
         */
        if (this.homeSiteId) {
            this.onCancel.emit();
            return;
        }
        this.applyFilters.emit(null);
    }

    apply(): void {
        const selectedOptionsList: Array<string> = this.innerOptionsList
            .filter((option: IOption) => option.checked)
            .map((option: IOption) => option.key);
        this.applyFilters.emit(this.isAllChecked ? [] : selectedOptionsList);
    }

    setFilterQuery(query: string): void {
        const trimmedQuery: string = query.trim();
        if (!trimmedQuery.length) {
            this.filterQuery = '';
            this.pipeFilterQuery = '';
        }

        this.pipeFilterQuery = (trimmedQuery.length < this.minQueryLength) ? '' : trimmedQuery;
    }

}
